import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartProps, BenchmarkMetric } from './types';
import { getPerformanceLevel, calculateAbsoluteDifference } from './benchmarkThresholds';

// Polar Chart Component
export const PolarChart: React.FC<ChartProps> = ({ metrics, chartId = 'polar' }) => {
  const chartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef2 = useRef<SVGSVGElement>(null);
  const containerRef2 = useRef<HTMLDivElement>(null);

  // Split metrics if there are more than 12
  const shouldSplit = useMemo(() => metrics.length > 12, [metrics.length]);
  const firstHalf = useMemo(() => shouldSplit ? metrics.slice(0, 12) : metrics, [shouldSplit, metrics]);
  const secondHalf = useMemo(() => shouldSplit ? metrics.slice(12) : [], [shouldSplit, metrics]);

  const getPerformanceColors = useCallback((brandValue: number, industryMean: number) => {
    const absoluteDifference = calculateAbsoluteDifference(brandValue, industryMean);
    const perfLevel = getPerformanceLevel(absoluteDifference);

    const colorMaps = {
      'excellent': { color: '#16a34a', bg: '#dcfce7', performance: 'excellent' as const },
      'very-good': { color: '#22c55e', bg: '#ecfccb', performance: 'very-good' as const },
      'good': { color: '#4ade80', bg: '#f0fdf4', performance: 'good' as const },
      'fair': { color: '#f59e0b', bg: '#fef3c7', performance: 'fair' as const },
      'poor': { color: '#f97316', bg: '#fed7aa', performance: 'poor' as const },
      'critical': { color: '#dc2626', bg: '#fecaca', performance: 'critical' as const }
    };

    return colorMaps[perfLevel.performance];
  }, []);

  const renderChart = useCallback((svgRef: React.RefObject<SVGSVGElement>, containerRef: React.RefObject<HTMLDivElement>, data: BenchmarkMetric[], id: string) => {
    if (!svgRef.current || !containerRef.current || data.length === 0) return;

    const container = containerRef.current;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerRect = container.getBoundingClientRect();
    const size = Math.min(containerRect.width, shouldSplit ? 400 : 500);
    const radius = (size / 2) - 60;
    const centerX = size / 2;
    const centerY = size / 2;

    svg.attr('width', size).attr('height', size);

    // Create tooltip
    const tooltip = d3.select('body').selectAll(`.polar-tooltip-${id}`)
      .data([null])
      .join('div')
      .attr('class', `polar-tooltip polar-tooltip-${id}`)
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('z-index', '1000')
      .style('pointer-events', 'none');

    // Calculate dynamic scale based on actual data range
    const allValues = data.flatMap(d => [d.value, d.industryMean || 0]).filter(v => v > 0);
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const valueRange = maxValue - minValue;

    // Add padding to the scale (20% on each side)
    const padding = Math.max(valueRange * 0.2, 5); // At least 5 percentage points padding
    const scaleMin = Math.max(0, minValue - padding);
    const scaleMax = maxValue + padding;

    const radiusScale = d3.scaleLinear()
      .domain([scaleMin, scaleMax])
      .range([0, radius]);

    const angleScale = d3.scaleBand()
      .domain(data.map(d => d.categoryName))
      .range([0, 2 * Math.PI])
      .paddingInner(0.1);

    // Create background circles
    const g = svg.append('g')
      .attr('transform', `translate(${centerX}, ${centerY})`);

    // Add concentric circles for grid - use 4 evenly spaced circles
    const gridCount = 4;
    const gridValues = Array.from({ length: gridCount }, (_, i) =>
      scaleMin + ((scaleMax - scaleMin) * (i + 1) / gridCount)
    );

    gridValues.forEach((value, index) => {
      const r = radiusScale(value);
      g.append('circle')
        .attr('r', r)
        .attr('fill', 'none')
        .attr('stroke', '#e2e8f0')
        .attr('stroke-width', index === gridCount - 1 ? 2 : 1)
        .attr('opacity', 0.3);

      // Only add label to the outermost circle
      if (index === gridCount - 1) {
        g.append('text')
          .attr('x', r + 5)
          .attr('y', 4)
          .attr('text-anchor', 'start')
          .attr('font-size', '11px')
          .attr('font-weight', '600')
          .attr('fill', '#6b7280')
          .text(`${value.toFixed(0)}%`);
      }
    });

    // Add radial axes
    data.forEach((d, i) => {
      const angle = (angleScale(d.categoryName) || 0) + (angleScale.bandwidth() / 2);
      const x1 = Math.cos(angle - Math.PI / 2) * radius;
      const y1 = Math.sin(angle - Math.PI / 2) * radius;

      g.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', x1)
        .attr('y2', y1)
        .attr('stroke', '#e2e8f0')
        .attr('stroke-width', 1)
        .attr('opacity', 0.5);

      // Add labels
      const labelRadius = radius + 25;
      const labelX = Math.cos(angle - Math.PI / 2) * labelRadius;
      const labelY = Math.sin(angle - Math.PI / 2) * labelRadius;

      const label = g.append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('fill', '#475569')
        .text(d.categoryName.length > 12 ? d.categoryName.substring(0, 12) + '...' : d.categoryName);

      // Add tooltip for truncated labels
      if (d.categoryName.length > 12) {
        label.append('title').text(d.categoryName);
      }
    });

    // Add industry average area (gray)
    const validData = data.filter(d => d.industryMean && d.industryMean > 0);
    if (validData.length > 0) {
      const industryLine = d3.lineRadial<BenchmarkMetric>()
        .angle(d => (angleScale(d.categoryName) || 0) + (angleScale.bandwidth() / 2))
        .radius(d => radiusScale(d.industryMean || 0))
        .curve(d3.curveLinearClosed);

      g.append('path')
        .datum(validData)
        .attr('d', industryLine)
        .attr('fill', '#94a3b8')
        .attr('fill-opacity', 0.15)
        .attr('stroke', '#94a3b8')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5');
    }

    // Add brand performance area
    if (validData.length > 0) {
      // Calculate overall performance to determine area color
      const averagePerformance = validData.reduce((sum, d) => {
        const absoluteDiff = calculateAbsoluteDifference(d.value, d.industryMean || 0);
        return sum + absoluteDiff;
      }, 0) / validData.length;

      // Get performance-based colors for the brand area
      const getAreaColors = (avgPerf: number) => {
        if (avgPerf >= 3) {
          return { fill: '#22c55e', stroke: '#16a34a', opacity: 0.25 }; // Green - strong performance
        } else if (avgPerf >= 1) {
          return { fill: '#84cc16', stroke: '#65a30d', opacity: 0.25 }; // Light green - above average
        } else if (avgPerf >= -1) {
          return { fill: '#3b82f6', stroke: '#2563eb', opacity: 0.2 }; // Blue - neutral/mixed
        } else if (avgPerf >= -3) {
          return { fill: '#f59e0b', stroke: '#d97706', opacity: 0.25 }; // Orange - below average
        } else {
          return { fill: '#ef4444', stroke: '#dc2626', opacity: 0.25 }; // Red - poor performance
        }
      };

      const areaColors = getAreaColors(averagePerformance);

      const brandLine = d3.lineRadial<BenchmarkMetric>()
        .angle(d => (angleScale(d.categoryName) || 0) + (angleScale.bandwidth() / 2))
        .radius(d => radiusScale(d.value))
        .curve(d3.curveLinearClosed);

      g.append('path')
        .datum(validData)
        .attr('d', brandLine)
        .attr('fill', areaColors.fill)
        .attr('fill-opacity', areaColors.opacity)
        .attr('stroke', areaColors.stroke)
        .attr('stroke-width', 3);
    }

    // Add industry average data points
    data.forEach((d, i) => {
      if (d.industryMean && d.industryMean > 0) {
        const angle = (angleScale(d.categoryName) || 0) + (angleScale.bandwidth() / 2);
        const r = radiusScale(d.industryMean);
        const x = Math.cos(angle - Math.PI / 2) * r;
        const y = Math.sin(angle - Math.PI / 2) * r;

        g.append('circle')
          .attr('cx', x)
          .attr('cy', y)
          .attr('r', 4)
          .attr('fill', '#94a3b8')
          .attr('stroke', 'white')
          .attr('stroke-width', 2);
      }
    });

    // Add brand performance data points
    data.forEach((d, i) => {
      const angle = (angleScale(d.categoryName) || 0) + (angleScale.bandwidth() / 2);
      const r = radiusScale(d.value);
      const x = Math.cos(angle - Math.PI / 2) * r;
      const y = Math.sin(angle - Math.PI / 2) * r;
      const colors = getPerformanceColors(d.value, d.industryMean || 0);

      g.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', 6)
        .attr('fill', colors.color)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .on('mouseover', function(event) {
          const hasIndustryData = d.industryMean && d.industryMean > 0;
          const absoluteDifference = hasIndustryData ? calculateAbsoluteDifference(d.value, d.industryMean || 0) : 0;

          const performanceText = !hasIndustryData ? 'Current Score' :
                                 colors.performance === 'excellent' ? 'Outstanding Performance' :
                                 colors.performance === 'very-good' ? 'Strong Performance' :
                                 colors.performance === 'good' ? 'Above Average' :
                                 colors.performance === 'fair' ? 'Below Average' :
                                 colors.performance === 'poor' ? 'Needs Improvement' : 'Urgent Attention Required';

          tooltip.html(`
            <div style="
              background: ${colors.bg};
              border: 2px solid ${colors.color};
              border-radius: 12px;
              padding: 16px;
              min-width: 200px;
              box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.25);
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            ">
              <div style="font-weight: 700; font-size: 14px; color: #1e293b; margin-bottom: 8px;">
                ${d.categoryName}
              </div>
              <div style="color: #475569; margin-bottom: 8px; font-size: 12px;">
                ${performanceText}
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Brand Score:</span>
                <span style="font-weight: 600; color: #1e293b; font-size: 12px;">${d.value.toFixed(1)}%</span>
              </div>
              ${hasIndustryData ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Industry Avg:</span>
                <span style="font-weight: 600; color: #64748b; font-size: 12px;">${d.industryMean?.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #64748b; font-size: 12px;">Difference:</span>
                <span style="font-weight: 600; color: ${absoluteDifference >= 0 ? colors.color : '#dc2626'}; font-size: 12px;">
                  ${absoluteDifference >= 0 ? '+' : ''}${absoluteDifference.toFixed(1)}%
                </span>
              </div>
              ` : ''}
            </div>
          `)
          .style('visibility', 'visible');

          d3.select(this).attr('r', 8);
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseout', function() {
          tooltip.style('visibility', 'hidden');
          d3.select(this).attr('r', 6);
        });
    });

    // Cleanup function
    return () => {
      d3.select('body').select(`.polar-tooltip-${id}`).remove();
    };
  }, [getPerformanceColors, shouldSplit]);

  useEffect(() => {
    const cleanup1 = renderChart(chartRef, containerRef, firstHalf, `${chartId}-1`);
    const cleanup2 = shouldSplit ? renderChart(chartRef2, containerRef2, secondHalf, `${chartId}-2`) : undefined;

    // Cleanup function
    return () => {
      cleanup1?.();
      cleanup2?.();
    };
  }, [firstHalf, secondHalf, chartId, shouldSplit, renderChart]);

  if (shouldSplit) {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', gap: '32px', justifyContent: 'center', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
              Metrics 1-12
            </h4>
            <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center' }}>
              <svg ref={chartRef}></svg>
            </div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
              Metrics 13-{metrics.length}
            </h4>
            <div ref={containerRef2} style={{ display: 'flex', justifyContent: 'center' }}>
              <svg ref={chartRef2}></svg>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <svg ref={chartRef}></svg>
    </div>
  );
};